/* @flow */
import * as React from 'react';
import { graphql, compose } from 'react-apollo';
import gql from 'graphql-tag';
import idx from 'idx';
import invariant from 'invariant';

import AddGroupOwnerModal from 'groups/components/AddGroupOwnerModal.jsx';
import Button from 'components/Button.jsx';
import Feature from 'components/Feature.jsx';
import Group from 'entities/Group';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator.jsx';
import ModalTrigger from 'components/ModalTrigger.jsx';
import PeopleTable from 'components/PeopleTable.jsx';
import PeopleList from 'components/PeopleList';
import View from 'components/View.jsx';
import { withRouter } from 'app/context/Router.jsx';

type Props = {
  orgId: number,
  group: Group,
  data: ReactApolloData<AdminPeopleTabQuery>,
  router: *,
};

function GroupPeopleTable(props: Props) {
  const { data, group } = props;

  if (group.isRoot()) {
    props.router.push('/classes/r/messages');
  }

  if (data && data.loading) {
    return <CenteredLoadingIndicator />;
  }

  const organization = data && data.organizations ? data.organizations[0] : null;
  const adminConsoleAccess = idx(organization, _ => _.permissions.adminConsole.accessConsole);

  const addOwnerButton = (
    // $FlowIssue inferring onRequestHide
    <ModalTrigger data-test="add-owner-button" modal={<AddGroupOwnerModal group={group} />}>
      <Button
        level="secondary"
        size="small"
        eventElementName="GroupMembersTabList-header-actions-addOwner">
        Add owner
      </Button>
    </ModalTrigger>
  );

  function OldPeopleTable() {
    return (
      <View className="GroupPeopleTable" scroll gutter="small">
        <PeopleTable
          adminConsoleAccess={adminConsoleAccess}
          group={group}
          namespace={group.getUuid()}
          headerActions={group.canAddOwner() ? addOwnerButton : null}
        />
      </View>
    );
  }

  function NewAdminPeopleTable() {
    if (organization) {
      return <PeopleList entityType="organization" entityUuid={organization.uuid} />;
    } else {
      const groupUuid = group.getUuid();
      invariant(groupUuid, 'Tried to render group people table without a group uuid!');
      return <PeopleList entityType="group" entityUuid={groupUuid} />;
    }
  }

  return adminConsoleAccess ? (
    <Feature
      name="2018.dec.web.people_list"
      on={<NewAdminPeopleTable data-test="adminPeopleTable" />}
      off={<OldPeopleTable data-test="groupPeopleTable" />}
    />
  ) : (
    <OldPeopleTable data-test="groupPeopleTable" />
  );
}

const withOrganizationAndClass = graphql(
  gql`
    query AdminPeopleTab($organizationId: Int) {
      organizations(ids: [$organizationId]) {
        id
        uuid
        permissions {
          adminConsole {
            accessConsole
          }
        }
      }
    }
  `,
  {
    skip: ({ orgId }) => !orgId,
    options: ({ orgId }) => ({
      variables: { organizationId: orgId },
    }),
  }
);

export { GroupPeopleTable };
export default compose(
  withOrganizationAndClass,
  withRouter
)(GroupPeopleTable);
